import React from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Link from '~/components/atoms/Link/Link'
import Lozenge from '~/components/atoms/Lozenge/Lozenge'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import LozengeList from '~/components/blocks/LozengeList/LozengeList'
import type { ReportCardProps } from '~/components/blocks/ReportCard/ReportCard'
import ReportCard from '~/components/blocks/ReportCard/ReportCard'
import { ComponentTheme } from '~/components/themes'

import styles from './ReportsContainer.module.css'

export interface ReportsContainerProps {
  reports: ReportCardProps[]
  title: string
  filterCategories?: {
    slug: string
    name: string
  }[]
  noPaddingBottom?: boolean
  slider?: boolean
  bigFeatureIndex?: number
  consise?: boolean
  theme?: ComponentTheme
  viewAllLink?: boolean
}

export default function ReportsContainer({
  reports,
  title,
  filterCategories,
  noPaddingBottom,
  slider,
  bigFeatureIndex,
  consise = false,
  theme = ComponentTheme.primary,
  viewAllLink = false,
}: ReportsContainerProps) {
  const router = useRouter()
  const activeCategoryFilter = router.query.category as string | undefined
  const filteredReports =
    activeCategoryFilter && filterCategories
      ? [...reports].filter(
          item => item.primaryCategory.slug === activeCategoryFilter
        )
      : reports
  const tCommon = useTranslations('common')

  // Update URL when filter changes
  function filterClickHandler(slug: string) {
    const current = new URLSearchParams(router.query as Record<string, string>)

    if (slug === activeCategoryFilter) {
      current.delete('category')
    } else {
      current.set('category', slug)
    }

    router.push(
      {
        pathname: router.pathname,
        query: current.toString(),
      },
      undefined,
      { shallow: true } // Don't trigger getServerSideProps/getStaticProps
    )
  }

  const renderReports = () => {
    let variant: ReportCardProps['variant'] = 'default'

    if (consise) {
      variant = 'consise'
    }

    if (slider) {
      return filteredReports.map((report, i) => (
        <div
          key={report._id}
          className={classNames(
            styles.sliderCard,
            i === 0 && styles.sliderCardFirst
          )}
        >
          <ReportCard {...report} variant={variant} matchGridHeight={consise} />
        </div>
      ))
    }
    return filteredReports.map((report, i) => {
      return (
        <ReportCard
          {...report}
          key={`report-card-${i}`}
          variant={
            bigFeatureIndex && bigFeatureIndex === i ? 'bigFeature' : 'default'
          }
        />
      )
    })
  }
  const renderGrid = () => {
    return <div className={styles.grid}>{renderReports()}</div>
  }

  return (
    <Fixture
      verticalPadding={true}
      noPaddingBottom={noPaddingBottom}
      theme={theme}
      className={styles.mainwrap}
    >
      <Container>
        <div className={styles.base}>
          <div className={styles.header}>
            <Text
              Tag="h3"
              size={TextSizes.primaryTitleMed}
              className={classNames(
                styles.sectionTitle,
                filterCategories && styles.reducedMargin
              )}
            >
              {title}
            </Text>
            {viewAllLink && (
              <Link href={`/market-reports`}>
                <Text
                  size={TextSizes.primaryTitleXSml}
                  className={styles.viewAll}
                >
                  {tCommon('viewAll')}
                </Text>
              </Link>
            )}
          </div>
          {filterCategories && (
            <div className={styles.filters}>
              <LozengeList>
                {filterCategories.map(item => (
                  <Lozenge
                    key={`lozenge-${item.slug}`}
                    title={item.name}
                    onClick={() => filterClickHandler(item.slug)}
                    isActive={activeCategoryFilter === item.slug}
                    count={activeCategoryFilter === item.slug ? 'x' : ''}
                  />
                ))}
              </LozengeList>
            </div>
          )}
          {filteredReports && filteredReports.length > 0 ? (
            <>
              {slider ? (
                <div className={styles.sliderWrapper}>
                  <DynamicSlider
                    slidesPerView={1.22}
                    slidesPerViewMed={2.1}
                    slidesPerViewLrg={3}
                    slidesPerViewHuge={consise ? 4 : 3}
                    overflowVisible={true}
                    spaceBetween={32}
                    hideArrowsMobile
                    hideTitle
                    spacer={false}
                    wrapperClass={styles.sliderWrapperClass}
                  >
                    {renderReports()}
                  </DynamicSlider>
                </div>
              ) : (
                renderGrid()
              )}
            </>
          ) : null}
        </div>
      </Container>
    </Fixture>
  )
}
